import {
  ActionButton,
  Edit,
  Ellipsis,
  Menu,
  PaletteCircle,
  Trash,
  useTheme,
} from '@sparemin/blockhead';
import cn from 'classnames';
import React, { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { Scissors } from 'components/icons';
import { pushModal } from 'redux/modules/modal';
import { Dispatch } from 'redux/types';
import { ClipSuggestionDislikeReason } from 'types';
import { useClipSelect } from '../ClipSelectContext';
import { ClipSelectAction } from './types';
import { editPanelBlock } from './utils';

export type ClipSelectActionsProps = {
  className?: string;
  clipId: number;
  clipName: string;
  isDisabled?: boolean;
  onEdit: () => void;
};

const ClipSelectActions: React.FunctionComponent<ClipSelectActionsProps> = ({
  className,
  clipId,
  clipName,
  isDisabled,
  onEdit,
}) => {
  const {
    suggestion,
    onEditTemplate,
    onDislikeClip,
    updateSuggestionProjectName,
  } = useClipSelect({
    clipId,
  });

  const theme = useTheme();
  const dispatch = useDispatch<Dispatch>();

  const handleRename = useCallback(async () => {
    const projectName = await dispatch(
      pushModal({
        name: 'RenameClip',
        params: { projectId: suggestion?.projectUuid, clipName },
      }),
    );

    if (projectName) {
      updateSuggestionProjectName(projectName);
    }
  }, [
    clipName,
    dispatch,
    suggestion?.projectUuid,
    updateSuggestionProjectName,
  ]);

  const handleOptionClick = useCallback(
    (option: ClipSelectAction) => {
      switch (option) {
        case 'remove':
          onDislikeClip(ClipSuggestionDislikeReason.NotInteresting, suggestion);
          break;

        case 'design':
          onEditTemplate();
          break;

        case 'edit':
          onEdit();
          break;

        case 'rename':
          handleRename();
          break;

        default:
          break;
      }
    },
    [handleRename, onEdit, onEditTemplate, onDislikeClip, suggestion],
  );

  return (
    <Menu
      placement="bottom end"
      density="comfortable"
      offset={10}
      button={
        <ActionButton
          className={cn(editPanelBlock('action-button'), className)}
          color={theme.palette.actionSecondary.main}
          isDisabled={isDisabled}
          size="large"
          variant="solid"
        >
          <Ellipsis />
        </ActionButton>
      }
      onAction={handleOptionClick}
      items={[
        { key: 'rename', icon: <Edit />, label: 'Rename' },
        { key: 'design', icon: <PaletteCircle />, label: 'Design' },
        { key: 'edit', icon: <Scissors height={14} />, label: 'Edit' },
        { key: 'remove', icon: <Trash />, label: 'Remove' },
      ]}
    >
      {item => (
        <Menu.Item key={item.key} icon={item.icon}>
          {item.label}
        </Menu.Item>
      )}
    </Menu>
  );
};

export default ClipSelectActions;
